import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatINR, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, coupon, setCoupon } = useCart();
  const [code, setCode] = useState(coupon?.code || "");
  const navigate = useNavigate();

  const discount = coupon?.discount || 0;
  const shipping = subtotal === 0 ? 0 : (subtotal >= 999 ? 0 : 79);
  const total = Math.max(0, subtotal - discount) + shipping;

  const applyCoupon = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal });
      setCoupon({ code: data.code, discount: data.discount });
      toast.success(`Coupon ${data.code} applied · You save ${formatINR(data.discount)}`);
    } catch (e) {
      setCoupon(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid coupon");
    }
  };

  useEffect(() => {
    // Re-validate if subtotal changes
    if (coupon?.code && subtotal > 0) {
      api.post("/coupons/validate", { code: coupon.code, subtotal })
        .then(({ data }) => setCoupon({ code: data.code, discount: data.discount }))
        .catch(() => setCoupon(null));
    }
    // eslint-disable-next-line
  }, [subtotal]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="font-serif text-4xl mb-2">Your cart is quiet</h1>
        <p className="text-muted-foreground mb-6">Let's find a scent worth lighting up.</p>
        <Link to="/shop"><Button className="rounded-full">Browse candles</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <h1 className="font-serif text-4xl sm:text-5xl mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4" data-testid="cart-items-list">
          {items.map((it, i) => (
            <div key={i} data-testid={`cart-item-${it.productId}`} className="flex gap-4 p-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
              <img src={it.image} alt={it.name} className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-3">
                  <div>
                    <div className="font-serif text-lg leading-tight">{it.name}</div>
                    <div className="text-xs text-muted-foreground">{it.scent}</div>
                    {it.variant && Object.values(it.variant).length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {Object.values(it.variant).map((v) => v.name).join(" · ")}
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeItem(i)} data-testid={`cart-remove-${it.productId}`}
                    className="p-2 rounded-full hover:bg-[hsl(var(--muted))] text-muted-foreground hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-1 border border-[hsl(var(--border))] rounded-full px-1 py-1">
                    <button onClick={() => updateQty(i, -1)} className="w-7 h-7 rounded-full hover:bg-[hsl(var(--muted))] flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-7 text-center text-sm font-medium">{it.quantity}</span>
                    <button onClick={() => updateQty(i, 1)} className="w-7 h-7 rounded-full hover:bg-[hsl(var(--muted))] flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="font-serif text-lg">{formatINR(it.unitPrice * it.quantity)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="lg:sticky lg:top-24 self-start p-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] space-y-4">
          <div className="font-serif text-2xl">Order Summary</div>

          <div className="space-y-2 py-3 border-t border-b border-[hsl(var(--border))]">
            <Row label="Subtotal" value={formatINR(subtotal)} />
            {discount > 0 && <Row label={`Discount (${coupon?.code})`} value={`−${formatINR(discount)}`} accent />}
            <Row label="Shipping" value={shipping === 0 ? "Free" : formatINR(shipping)} />
            <Row label="Estimated Tax" value="Calculated at checkout" small />
          </div>
          <div className="flex justify-between items-baseline">
            <div className="text-sm font-medium">Total</div>
            <div className="font-serif text-2xl">{formatINR(total)}</div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Tag className="w-3 h-3" />Coupon</div>
            <div className="flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="coupon-code-input"
                placeholder="Enter code" className="flex-1 px-3 py-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm uppercase" />
              <Button data-testid="coupon-apply-button" onClick={applyCoupon} variant="outline" className="rounded-full">Apply</Button>
            </div>
            <div className="text-xs text-muted-foreground mt-2">Try <b>WELCOME10</b>, <b>GLOW20</b>, <b>COZY200</b></div>
          </div>

          <Button data-testid="cart-checkout-button" onClick={() => navigate("/checkout")} size="lg"
            className="w-full rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
            Proceed to Checkout
          </Button>
          <Link to="/shop" className="block text-center text-sm text-muted-foreground hover:text-foreground">Continue shopping</Link>
        </aside>
      </div>
    </div>
  );
}

const Row = ({ label, value, accent, small }) => (
  <div className="flex justify-between text-sm">
    <div className={small ? "text-muted-foreground" : ""}>{label}</div>
    <div className={accent ? "text-candle-terracotta font-medium" : "font-medium"}>{value}</div>
  </div>
);
